<template>
    <v-card
        class="fill-height"
        :class="{ 'in-modal': inModal }"
        flat
    >
        <Dimmer
            :hide-spinner="false"
            :model-value="progress"
        >
            <template #content>
                <form
                    @submit="onSubmit($event)"
                    :class="{ 'd-flex flex-column h-100': inModal }"
                    novalidate
                >
                    <v-card-text
                        v-if="submitSuccess"
                        class="text-body-1"
                    >
                        <v-alert
                            type="success"
                        >
                            Thank you for submitting your team's information! A representative will be in touch with you soon regarding the status of your application. Thanks for choosing {{ brandTitle }}!
                        </v-alert>
                    </v-card-text>
                    <v-card-text
                        v-else
                        :class="{ 'mt-6 px-4 pb-0': !flat, 'ma-0 px-0': flat }"
                    >

                        <!-- Team Name -->
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="teamName"
                                    v-bind="teamNameProps"
                                    name="teamName"
                                    label="Team Name"
                                    autocomplete="organization"
                                    variant="outlined"
                                    hide-details
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Sport Type / Gender -->
                        <v-row>
                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <v-select
                                    v-if="!lo_isError(teamsSportsObj)"
                                    v-model="sport"
                                    v-bind="sportProps"
                                    :items="teamsSportsObj?.data"
                                    item-title="name"
                                    item-value="code"
                                    name="sport"
                                    label="Sport"
                                    hide-details
                                    variant="outlined"
                                    required
                                ></v-select>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <label
                                    class="d-block font-weight-medium"
                                >
                                    Team Gender
                                </label>
                                <v-radio-group
                                    v-model="teamGender"
                                    v-bind="teamGenderProps"
                                    class="fill-width"
                                    name="teamGender"
                                    inline
                                >
                                    <v-radio
                                        label="Men's"
                                        value="mens"
                                    ></v-radio>
                                    <v-radio
                                        label="Women's"
                                        value="womens"
                                    ></v-radio>
                                    <v-radio
                                        label="Coed"
                                        value="coed"
                                    ></v-radio>
                                </v-radio-group>
                            </v-col>
                        </v-row>

                        <!-- Team Level -->
                        <v-row>
                            <v-col>
                                <v-select
                                    v-if="!lo_isError(teamsLevelsObj)"
                                    v-model="level"
                                    v-bind="levelProps"
                                    :items="teamsLevelsObj?.data"
                                    item-title="name"
                                    item-value="code"
                                    name="level"
                                    label="Team Level"
                                    hide-details
                                    variant="outlined"
                                    required
                                ></v-select>
                            </v-col>
                        </v-row>

                        <v-divider
                            class="my-8"
                        ></v-divider>

                        <!-- Country -->
                        <v-row>
                            <v-col>
                                <v-autocomplete
                                    v-model="country"
                                    v-bind="countryProps"
                                    :items="CountriesList"
                                    item-title="country"
                                    item-value="cca2"
                                    name="country"
                                    label="Country"
                                    autocomplete="country"
                                    hide-details="auto"
                                ></v-autocomplete>
                            </v-col>
                        </v-row>

                        <!-- City / State -->
                        <v-row>
                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="city"
                                    v-bind="cityProps"
                                    name="city"
                                    label="City / Town"
                                    type="text"
                                    autocorrect="off"
                                    autocomplete="address-level2"
                                    variant="outlined"
                                    hide-details="auto"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <!-- STATE: USA -->
                                <template
                                    v-if="formValues.country === 'US'"
                                >
                                    <v-autocomplete
                                        v-model="state"
                                        v-bind="stateProps"
                                        :items="statesList"
                                        name="state"
                                        label="State"
                                        autocomplete="address-level1"
                                        hide-details="auto"
                                    ></v-autocomplete>
                                </template>
                                <!-- STATE: International -->
                                <template
                                    v-else
                                >
                                    <v-text-field
                                        v-model="state"
                                        v-bind="stateProps"
                                        name="state"
                                        label="State / Provence / Region"
                                        autocomplete="address-level1"
                                        variant="outlined"
                                        hide-details="auto"
                                    ></v-text-field>
                                </template>
                            </v-col>
                        </v-row>

                        <v-divider
                            class="my-8"
                        ></v-divider>

                        <!-- Website -->
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="website"
                                    v-bind="websiteProps"
                                    name="website"
                                    label="Website URL"
                                    autocorrect="off"
                                    autocapitalize="none"
                                    variant="outlined"
                                    hide-details
                                    required
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Logo
                        <v-row>
                            <v-col>
                                <v-file-input
                                    :label="`${fieldsFormat === 'teams' ? 'Team' : 'Brand'} Logo`"
                                    :messages="['Recommended file type .svg, .ai., .pdf if available']"
                                    variant="outlined"
                                >
                                    <template #message="{ message }">
                                        <p class="text-body-2">{{ message }}</p>
                                    </template>
                                </v-file-input>
                            </v-col>
                        </v-row>
                        -->

                        <!-- Message -->
                        <v-row>
                            <v-col>
                                <p class="text-body-1">How can StringKing help your program?</p>
                                <v-textarea
                                    v-model="message"
                                    v-bind="messageProps"
                                    name="message"
                                    label="Message"
                                    counter="5000"
                                    variant="outlined"
                                ></v-textarea>
                            </v-col>
                        </v-row>

                        <!-- Contacts -->
                        <v-row
                            dense
                        >
                            <v-col
                                cols="12"
                                class="pb-0"
                            >
                                <p class="text-body-1">First add your contact information, then any additional members.</p>
                            </v-col>
                            <v-col
                                cols="12"
                            >
                                <fieldset
                                    class="simple-fieldset rounded px-2 pb-8"
                                >
                                    <legend
                                        class="text-body-1 font-weight-medium"
                                    >
                                        Your Contact Info
                                    </legend>

                                    <OrgContactForm
                                        ref="elContactInfoMain"
                                        v-model="contactInfoMain"
                                        :in-modal="true"
                                        :roles="roles"
                                        @on-save="onSaveContact($event)"
                                        is-nested-form
                                        hide-toolbar
                                        hide-actions
                                    ></OrgContactForm>
                                </fieldset>
                            </v-col>
                            <v-col
                                cols="12"
                            >
                                <fieldset
                                    class="simple-fieldset rounded pa-6"
                                >
                                    <legend>
                                        <span
                                            class="text-body-1 font-weight-medium"
                                        >
                                            Other Contacts
                                        </span>
                                        <span
                                            class="font-weight-regular font-size-8"
                                        >
                                            (optional)
                                        </span>
                                    </legend>

                                    <template
                                        v-for="(contact, index) in contactInfoOthers"
                                        :key="index"
                                    >
                                        <v-input
                                            class="align-center fill-width"
                                            :class="{ 'mt-2': index > 0 }"
                                            :append-icon="mdiMinusCircle"
                                            @click:append="removeContact(index)"
                                            hide-details
                                        >
                                            <v-row dense>
                                                <v-col
                                                    cols="12"
                                                    md="6"
                                                    class="d-flex flex-column justify-center font-weight-medium"
                                                >
                                                    {{ contact.firstName }} {{ contact.lastName }}
                                                </v-col>
                                                <v-col
                                                    cols="12"
                                                    md="6"
                                                    class="text-start text-md-end font-italic"
                                                >
                                                    {{ getRoleNameByUid(contact.role) }}
                                                </v-col>
                                            </v-row>
                                        </v-input>
                                    </template>
                                    <div
                                        class="d-flex justify-end"
                                        :class="{ 'mt-4': contactInfoOthers.length }"
                                    >
                                        <v-btn
                                            color="secondary"
                                            variant="text"
                                            :disabled="!contactInfoMain || !roles.length"
                                            @click.prevent="addContact"
                                        >
                                            Add Contact
                                        </v-btn>
                                    </div>
                                </fieldset>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-card-actions
                        v-if="!submitSuccess"
                        class="pa-0 mt-6"
                    >
                        <v-spacer></v-spacer>
                        <v-btn
                            variant="text"
                            color="secondary"
                            size="large"
                            @click="onExit()"
                        >
                            Cancel
                        </v-btn>
                        <v-btn
                            variant="flat"
                            color="primary"
                            type="submit"
                            size="large"
                        >
                            Send Request
                        </v-btn>
                    </v-card-actions>
                </form>
            </template>
        </Dimmer>

        <ResponsiveModal v-model="dialogContactItem">
            <template #default="{ openCount }">
                <OrgContactForm
                    :key="`user-contact-form-open-${openCount}`"
                    title="Add Contact"
                    :in-modal="true"
                    :roles="roles"
                    @on-save="onSaveContact($event)"
                    @on-exit="onExitContact()"
                ></OrgContactForm>
            </template>
        </ResponsiveModal>
    </v-card>
</template>

<script lang="ts" setup>
    import { toTypedSchema } from '@vee-validate/valibot';

    import type {
        PaginatedPayload,
        OrgSignUpContact,
        OrgTeamSignUpRequest,
        MembershipRole,
        TeamsSport,
        TeamsLevel
    } from '~/types';
    import {
        OrgTeamSignupFormModel as formModel
    } from '~/models';
    import Dimmer from 'x1-base/components/Dimmer/Dimmer.vue';
    import { isError as lo_isError } from 'es-toolkit';
    import { CountriesList } from '~/models/countries-list';

    import type { VSelect } from 'vuetify/components';
    import { useFormValidation, useGlobalAlert } from 'x1-base/composables';

    import {
        useOrgs,
        useApiCommon,
        useFileUtils,
        useUSStates
    } from '~/composables';

    import ResponsiveModal from 'x1-base/components/ResponsiveModal/ResponsiveModal.vue';
    import OrgContactForm from '~/components/onboarding/OrgContactForm/OrgContactForm.vue';
    import { mdiMinusCircle } from '@mdi/js';

    defineProps({
        inModal: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        flat: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        fieldsFormat: {
            type: String as PropType<'teams' | 'orgs' | 'retailers'>,
            required: true
        }
    });

    const emit = defineEmits<{
        (e: 'on-save', value: OrgTeamSignUpRequest): void;
        (e: 'on-success'): void;
        (e: 'on-exit'): void;
    }>();


    const $config = useRuntimeConfig();
    const brandTitle = $config.public.brand.title;
    const elContactInfoMain = shallowRef<InstanceType<typeof OrgContactForm>>();
    const progress = ref(false);
    const submitSuccess = ref(false);
    const dialogContactItem = ref<boolean | null>(false);
    const useApiCommonObj = useApiCommon();
    const { createOnboardingRequest } = useOrgs();
    // const { getEmptyFileObj } = useFileUtils();
    const { getDefineFieldOptions } = useFormValidation();
    const createGlobalAlert = useGlobalAlert();
    const statesList = useUSStates().getGroupedStatesList();

    const {
        handleSubmit,
        defineField,
        values: formValues,
        meta: formMeta,
        setFieldValue
    } = useForm<OrgTeamSignUpRequest>({
        validationSchema: toTypedSchema(
            formModel
        ),
        initialValues: {
            country: 'US'
        }
    });

    const vo = () => getDefineFieldOptions('vuetify');
    const [teamName, teamNameProps] = defineField('teamName', vo());
    const [sport, sportProps] = defineField('sport', vo());
    const [level, levelProps] = defineField('level', vo());
    const [teamGender, teamGenderProps] = defineField('teamGender', vo());
    const [city, cityProps] = defineField('city', vo());
    const [state, stateProps] = defineField('state', vo());
    const [country, countryProps] = defineField('country', vo());
    const [website, websiteProps] = defineField('website', vo());
    // const [logo, logoProps] = defineField('logo', vo());
    const [message, messageProps] = defineField('message', vo());

    const contactInfoMain = ref<Nilish<OrgSignUpContact>>(null);
    const contactInfoOthers = ref<OrgSignUpContact[]>([]);

    const unWatchContacts = watch(
        [contactInfoMain, contactInfoOthers],
        ([newMain, newOthers]) => {
            const contacts: OrgSignUpContact[] = [];

            if (newMain) {
                contacts.push(newMain, ...contactInfoOthers.value);
            }

            setFieldValue('contacts', contacts);
        },
        {
            deep: true
        }
    );

    const onSubmit = handleSubmit(async (values: OrgTeamSignUpRequest) => {
        progress.value = true;
        const response = await createOnboardingRequest<OrgTeamSignUpRequest>(values);
        progress.value = false;

        if (lo_isError(response)) {
            createGlobalAlert({
                text: 'Could not submit request. Please check form and try again.',
                show: true,
                type: 'error'
            });
            return;
        }

        emit('on-success');
        submitSuccess.value = true;
    }, () => {
        elContactInfoMain.value?.validateForm();

        createGlobalAlert({
            text: 'Please make sure all required fields are filled-in.',
            show: true,
            type: 'error'
        });
    });

    const { data: teamsSportsObj } = useLazyAsyncData<PaginatedPayload<TeamsSport> | Error | void>(
        'keyOrgTeamSportObj',
        () => useApiCommonObj.getPagedListItems<TeamsSport>('/teams/sports/')
    );

    const { data: teamsLevelsObj } = useLazyAsyncData<PaginatedPayload<TeamsLevel> | Error | void>(
        'keyOrgTeamLevelObj',
        () => useApiCommonObj.getPagedListItems<TeamsLevel>('/teams/levels/')
    );

    const { data: teamsRolesObj } = useLazyAsyncData<PaginatedPayload<MembershipRole> | Error | void>(
        'keyOrgTeamRolesObj',
        () => useApiCommonObj.getPagedListItems<MembershipRole>('/orgs/roles/', { params: { is_team: true } })
    );

    const roles = computed<MembershipRole[]>(() => !lo_isError(teamsRolesObj.value) ? teamsRolesObj.value?.data ?? [] : []);

    function getRoleNameByUid(uid: string): string {
        return roles.value.find((o) => o.uid === uid)?.name ?? '';
    }

    function addContact(e: Event): void {
        dialogContactItem.value = true;
    }

    function removeContact(idx: number): void {
        contactInfoOthers.value.splice(idx, 1);
    }

    function onSaveContact(contactObj: OrgSignUpContact): void {
        contactInfoOthers.value.push(contactObj);
        onExitContact();
    }

    function onExitContact(): void {
        dialogContactItem.value = false;
    }

    function onExit(): void {
        emit('on-exit');
    }

    onBeforeUnmount(() => {
        unWatchContacts();
    });
</script>

<style lang="scss" scoped></style>
